import React, { useMemo } from 'react';
import theme from 'theme';
import { Accordion, Colors, DropDown, LoadingSpinner, Text } from 'library';
import { Tooltip, styled, useMediaQuery } from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';

import { CredentialPill } from '../components/CredentialPill';
import { educationChoice, experienceChoice, singleChoice, multiChoice, booleanChoice } from '../utils/choices';
import { updateScale } from '../utils/scale';
import { findProblems } from '../utils/problems';
import { useRequirementsAndPayContext } from '../context';
import { DEGREE, ECE_DEGREE, STATE_SPECIFIC_REQUIREMENTS_TO_EXCLUDE, YEARS_EXPERIENCE } from '../constants';

import type { GenericChoice, Qualification } from '../types';
import { orderBy } from 'shared/arrays';
import { StateSpecificRequirements, hasStateSpecificRequirements } from './StateSpecificRequirements';

export function RequirementsQuestions() {
    const small = useMediaQuery(theme.breakpoints.down('lg'));

    const { qualifications, state, loading, initialScale, updateScales } = useRequirementsAndPayContext();
    const { education, experience, multi, single, boolean } = qualifications;
    const problems = findProblems(initialScale, state, true, '', qualifications);

    const anyProblem =
        problems?.education ||
        problems?.experience ||
        Object.values(problems?.boolean).includes(true) ||
        Object.values(problems?.single).includes(true) ||
        Object.values(problems?.multi).includes(true);

    function update(
        value: number | number[],
        choices: GenericChoice[],
        qualification: Qualification | null,
        category: 'education' | 'experience' | 'multiple' | 'single' | 'boolean',
    ) {
        if (!initialScale) return;

        const updatedScale = updateScale(initialScale, value, choices, qualification, category);
        updateScales(updatedScale);
    }

    const selectedCredentialCount = useMemo(() => {
        if (!initialScale) return 0;
        const baseNames = [DEGREE, ECE_DEGREE, YEARS_EXPERIENCE];
        const excluded = new Set(STATE_SPECIFIC_REQUIREMENTS_TO_EXCLUDE);
        const ids = new Set(
            initialScale.qualifications
                .filter((q) => !baseNames.includes(q.name))
                .filter((q) => !excluded.has(q.qualification_id))
                .map((q) => q.qualification_id),
        );
        return ids.size;
    }, [initialScale]);

    const credentialsNumber = hasStateSpecificRequirements(state) ? 4 : 3;
    const credentialsTitle =
        selectedCredentialCount > 0
            ? `${credentialsNumber}. Any other credentials required? (${selectedCredentialCount} selected)`
            : `${credentialsNumber}. Any other credentials required?`;

    const limitingCredentialNames: string[] = [
        ...(boolean ?? [])
            .filter((b) => problems?.boolean[`${b.key}`])
            .map((b) => b.qualification?.name)
            .filter((name): name is string => Boolean(name)),
        ...(single ?? []).filter((s) => problems?.single[`${s.qualification.id}`]).map((s) => s.qualification.name),
        ...(multi ?? []).filter((m) => problems?.multi[`${m.qualification.id}`]).map((m) => m.qualification.name),
    ];

    if (loading) return <LoadingSpinner />;

    if (!initialScale) return <Text>There was an error loading the requirements.</Text>;

    return (
        <div>
            <Question error={problems?.education?.toString()}>
                <div>
                    <Text bold textStyle={{ color: Colors.turquoise }}>
                        1. What is the <u>mininum level of education</u> required to work this position?
                    </Text>
                    <DropDown
                        selected={educationChoice(initialScale, education)}
                        fields={orderBy(education, 'order')}
                        onChange={({ target: { value } }) => update(Number(value), education, null, 'education')}
                        style={{ width: small ? '100%' : '60%' }}
                    />
                </div>
                {problems?.education && (
                    <Tooltip
                        title="The requirements selected will significantly limit the worker pool available. Consider lowering the requirements before continuing."
                        placement="left"
                        arrow
                    >
                        <WarningAmberRounded sx={{ height: 28, width: 28, color: Colors.terraCotta, cursor: 'help' }} />
                    </Tooltip>
                )}
            </Question>
            <Question>
                <div>
                    <Text bold textStyle={{ color: Colors.turquoise }}>
                        2. What is the <u>mininum level of experience</u> required to work this position?
                    </Text>
                    <DropDown
                        selected={experienceChoice(initialScale, experience)}
                        fields={experience}
                        onChange={({ target: { value } }) => update(Number(value), experience, null, 'experience')}
                        style={{ width: small ? '100%' : '60%' }}
                    />
                </div>
            </Question>
            <StateSpecificRequirements state={state} update={update} />
            <AccordionWrapper>
                <Accordion title={credentialsTitle} textStyle={{ color: Colors.turquoise }} defaultExpanded>
                    <Section small={small.toString()}>
                        {multi?.map((multi: { qualification: Qualification; choices: GenericChoice[] }) => (
                            <DropDownBox key={multi.qualification.id} small={small.toString()}>
                                <DropDown
                                    title={multi.qualification.name}
                                    name={multi.qualification.name}
                                    selected={multiChoice(initialScale, multi.qualification, multi.choices)}
                                    fields={multi.choices}
                                    multiple={true}
                                    onChange={({ target: { value } }) =>
                                        update(
                                            value as unknown as number[],
                                            multi.choices,
                                            multi.qualification,
                                            'multiple',
                                        )
                                    }
                                />
                            </DropDownBox>
                        ))}
                        {single
                            ?.filter(
                                (single: { qualification: Qualification }) =>
                                    !STATE_SPECIFIC_REQUIREMENTS_TO_EXCLUDE.includes(single.qualification.id),
                            )
                            .map((single: { qualification: Qualification; choices: GenericChoice[] }) => (
                                <DropDownBox key={single.qualification.id} small={small.toString()}>
                                    <DropDown
                                        title={single.qualification.name}
                                        name={single.qualification.name}
                                        selected={singleChoice(initialScale, single.qualification, single.choices)}
                                        fields={single.choices}
                                        onChange={({ target: { value } }) =>
                                            update(Number(value), single.choices, single.qualification, 'single')
                                        }
                                    />
                                </DropDownBox>
                            ))}
                    </Section>
                    <Section small={small.toString()}>
                        {boolean
                            ?.filter(
                                (bool: GenericChoice) => !STATE_SPECIFIC_REQUIREMENTS_TO_EXCLUDE.includes(bool.key),
                            )
                            .map((bool: GenericChoice) => (
                                <CredentialPill
                                    key={bool.key}
                                    label={bool.qualification?.name ?? ''}
                                    onClick={() => update(bool.key, [bool], null, 'boolean')}
                                    selected={booleanChoice(initialScale, bool)}
                                />
                            ))}
                    </Section>
                </Accordion>
            </AccordionWrapper>
            {anyProblem && (
                <WarningBox>
                    <WarningAmberRounded
                        sx={{ color: Colors.terraCotta, fontSize: 20, flexShrink: 0, marginTop: '2px' }}
                    />
                    <WarningContent>
                        <Text variant="body2" textStyle={{ color: Colors.darkNavy }}>
                            The requirements selected will significantly limit the worker pool available. Consider
                            lowering the requirements before continuing.
                        </Text>
                        {limitingCredentialNames.length > 0 && (
                            <Text variant="body2" textStyle={{ color: Colors.darkNavy, marginTop: 6 }} bold>
                                Credentials that will limit the worker pool: {limitingCredentialNames.join(', ')}
                            </Text>
                        )}
                    </WarningContent>
                </WarningBox>
            )}
        </div>
    );
}

const Question = styled('div')(({ error }: { error?: string }) => ({
    alignItems: 'center',
    backgroundColor: Colors.white,
    borderRadius: 10,
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
    padding: '5px 20px',
    ...(error === 'true' && {
        '& .MuiInput-underline:before': {
            borderBottomColor: Colors.terraCotta,
            borderBottomWidth: 2,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottomColor: Colors.terraCotta,
            borderBottomWidth: 2,
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: Colors.terraCotta,
        },
        '& .MuiSelect-select': {
            color: Colors.terraCotta,
        },
        '& .MuiSelect-icon': {
            color: Colors.terraCotta,
        },
    }),
}));

const DropDownBox = styled('div')(({ small }: { small?: string }) => ({
    width: small == 'true' ? '100%' : '40%',
    paddingRight: 20,
}));

const Section = styled('div')(({ small }: { small?: string }) => ({
    display: small === 'true' ? 'block' : 'flex',
    flexWrap: 'wrap',
}));

const AccordionWrapper = styled('div')({
    padding: '0 20px',
    '& .MuiAccordion-root': {
        margin: 0,
        '&:before': { display: 'none' },
    },
    '& .MuiAccordion-root.Mui-expanded': {
        margin: 0,
    },
    '& .MuiAccordionSummary-root': {
        minHeight: 'auto',
    },
    '& .MuiAccordionSummary-root.Mui-expanded': {
        minHeight: 'auto',
    },
    '& .MuiAccordionSummary-content': {
        margin: '8px 0',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
        margin: '8px 0',
    },
});

const WarningBox = styled('div')({
    marginTop: 20,
    marginLeft: 20,
    marginRight: 20,
    padding: '10px 14px',
    backgroundColor: Colors.lightTerraCotta,
    border: `1px solid ${Colors.terraCotta}`,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
});

const WarningContent = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});
